import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Wallet, Plus, Loader2, Info, Pencil, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import AddLiabilityModal from './AddLiabilityModal';
import FAQLink from '../faqs/FAQLink';
import { LIABILITY_TYPE_LABEL, LIABILITY_TYPE_COLOR } from './liabilityConstants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function fmt(v, cur) {
  if (v == null) return '—';
  try {
    return `${cur ? cur + ' ' : ''}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  } catch {
    return `${v}`;
  }
}

/**
 * Liabilities Card — the management surface for the user's debts.
 *
 * Lives on the Overview tab between Cashflow Calendar and Net Worth.
 * Every liability is shown in full detail (counterparty, balance, rate,
 * maturity) so the row is meaningful to banks and accountants reading the
 * forthcoming SoNW export.
 */
export default function LiabilitiesCard({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/liabilities`, { headers: authHeaders() });
      if (!resp.ok) throw new Error(`Failed to load (${resp.status})`);
      const data = await resp.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load liabilities');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSuccess = () => {
    load();
    onChange?.();
  };

  const handleDelete = async (liab) => {
    if (!window.confirm(`Delete "${liab.name}"? This can't be undone.`)) return;
    setDeletingId(liab.liability_id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/liabilities/${liab.liability_id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!resp.ok) throw new Error(`Delete failed (${resp.status})`);
      toast.success('Liability removed');
      setItems((prev) => prev.filter((x) => x.liability_id !== liab.liability_id));
      onChange?.();
    } catch (e) {
      toast.error(e.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card className="portfolio-card" data-testid="liabilities-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-rose-700 shrink-0" />
            <CardTitle className="text-base sm:text-xl font-serif">Liabilities</CardTitle>
            {items.length > 0 && (
              <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300 text-xs">
                {items.length} {items.length === 1 ? 'item' : 'items'}
              </Badge>
            )}
          </div>
          <Button
            type="button"
            size="sm"
            onClick={() => { setEditing(null); setModalOpen(true); }}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="liabilities-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add liability
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-3" data-testid="liabilities-empty">
            <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
            <span>
              No liabilities recorded. Add mortgages, loans, credit-card balances, margin loans, and tax owed
              so they feed into your Statement of Net Worth.
            </span>
          </div>
        ) : (
          <div className="space-y-2" data-testid="liabilities-list">
            {items.map((liab) => {
              const cls = LIABILITY_TYPE_COLOR[liab.liability_type] || 'bg-slate-50 text-slate-700 border-slate-200';
              const label = LIABILITY_TYPE_LABEL[liab.liability_type] || liab.liability_type;
              return (
                <div
                  key={liab.liability_id}
                  className="rounded-md border border-slate-200 bg-white px-3 py-2"
                  data-testid={`liability-row-${liab.liability_id}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-slate-900 text-sm">{liab.name}</span>
                        <Badge variant="outline" className={`text-[10px] ${cls}`}>{label}</Badge>
                        {liab.owner_type === 'related' && (
                          <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-300">Related</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {liab.counterparty && <span>{liab.counterparty}</span>}
                        {liab.interest_rate != null && <span> · {liab.interest_rate}% APR</span>}
                        {liab.maturity_date && <span> · matures {String(liab.maturity_date).slice(0, 10)}</span>}
                        {liab.due_date && <span> · due {String(liab.due_date).slice(0, 10)}</span>}
                        {liab.credit_limit != null && <span> · limit {fmt(liab.credit_limit, liab.currency)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <div className="text-xs text-slate-500">Outstanding</div>
                        <div className="text-sm font-semibold text-slate-900">
                          {fmt(liab.outstanding_balance, liab.currency)}
                        </div>
                      </div>
                      <Button type="button" size="sm" variant="ghost"
                              onClick={() => { setEditing(liab); setModalOpen(true); }}
                              className="h-7 w-7 p-0 text-slate-500 hover:text-emerald-700"
                              aria-label="Edit"
                              data-testid={`liability-edit-${liab.liability_id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button type="button" size="sm" variant="ghost"
                              onClick={() => handleDelete(liab)}
                              disabled={deletingId === liab.liability_id}
                              className="h-7 w-7 p-0 text-slate-500 hover:text-red-600"
                              aria-label="Delete"
                              data-testid={`liability-delete-${liab.liability_id}`}>
                        {deletingId === liab.liability_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <AddLiabilityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={handleSuccess}
        liability={editing}
      />
      <div className="px-6 pb-4">
        <FAQLink categoryKey="net-worth" />
      </div>
    </Card>
  );
}
